import { useAppStore } from 'v2/state/app'

/**
 * Enterprise CTA routing. Every gated CTA opens the Buddy conversation; nothing
 * here files a request.
 *
 * It used to fork: an AM-managed account (HubSpot owner assigned) had its intake
 * POSTed straight to core from the click handler, with only a toast to show for
 * it. That produced the leads Sales complained about — 70 of 87 in a month arrived
 * through the button carrying one canned sentence and already marked qualified,
 * and only 5 of those customers ever said anything in chat. Sales got no use case,
 * no volume, and usually no spend.
 *
 * So the button now starts the conversation and only a real conversation files the
 * request, carrying a use case and either a volume or an explicit note that the
 * customer would not give one. AM-managed accounts lose nothing: core resolves
 * `am_managed` server-side on whatever Buddy submits and still routes it to the
 * account manager, so the same person still owns the lead — they just receive a
 * qualified one. Someone who clicks and walks away no longer becomes a sales
 * request (Ayush, India Enterprise flow spec task 9, 2026-08-24).
 */

/**
 * Identifies the screen / CTA an enterprise (or enterprise add-on) request was
 * started from. Used to pre-fill the Buddy message with a clear, unambiguous
 * capability phrase so Buddy can classify the request (categories + channel)
 * and submit it to the sales/review n8n intake.
 *
 * Replaces the old in-console "Upgrade to Enterprise" / "Request access" HubSpot
 * modals — every gated CTA now opens Buddy instead. For seats/capacity, the
 * message is plan-neutral: Buddy branches on the user's plan (upgrade vs
 * existing-enterprise add-on).
 */
export type EnterpriseRequestSource =
  | 'plans-pricing'
  | 'account-limits'
  | 'sender-ids'
  | 'buy-number-country'
  | 'voice-geo'
  | 'messaging-geo'
  | 'sip-geo'
  | 'voice-feature'
  | 'compliance'
  | 'whatsapp'
  | 'verify'
  | 'messaging'
  | 'endpoints'
  | 'add-capacity'
  | 'human-specialist-seats'
  | 'recharge-limit'

export interface EnterpriseRequestContext {
  source: EnterpriseRequestSource
  /** Country name, for the "buy numbers in <country>" buy-number CTA. */
  country?: string
  /** Human label of a gated feature, for the generic feature-gate CTA (e.g. "Voice"). */
  featureLabel?: string
}

/**
 * Maps each source screen to the capability the user wants, phrased so Buddy
 * can unambiguously classify it. The leading "I'd like to " is added by
 * buildEnterprisePrompt.
 */
const SOURCE_PHRASES: Record<EnterpriseRequestSource, (ctx: EnterpriseRequestContext) => string> = {
  'plans-pricing': () => 'upgrade to the Enterprise plan',
  'account-limits': () => 'raise my monthly account usage limit',
  'sender-ids': () => 'use Sender IDs, which needs the Enterprise plan',
  'buy-number-country': (ctx) =>
    `buy phone numbers in ${ctx.country || 'another country'}, which needs the Enterprise plan`,
  'voice-geo': () =>
    'enable calling to more countries (voice geo permissions), which needs the Enterprise plan',
  'messaging-geo': () =>
    'enable messaging to more countries (messaging geo permissions), which needs the Enterprise plan',
  'sip-geo': () =>
    'enable SIP trunking calling to more countries (SIP trunking geo permissions), which needs the Enterprise plan',
  'voice-feature': (ctx) =>
    `get access to ${ctx.featureLabel || 'Voice'}, which needs the Enterprise plan`,
  compliance: () =>
    'register for US messaging compliance (10DLC / Toll-free), which needs the Enterprise plan',
  whatsapp: () => 'connect WhatsApp Business numbers, which needs the Enterprise plan',
  verify: () => 'use Verify (OTP authentication), which needs the Enterprise plan',
  messaging: () => 'use Messaging, which needs the Enterprise plan',
  endpoints: () =>
    'use Voice Endpoints (SIP endpoints for my agents), which needs the Enterprise plan',
  // Seats / capacity — plan-neutral; Buddy decides upgrade vs add-on from the user's plan.
  'add-capacity': () => 'add more call capacity (concurrent calls and RPS) to my account',
  'human-specialist-seats': () => 'add more Human Specialist seats to my account',
  'recharge-limit': () => 'raise my monthly recharge / spend limit',
}

/** Hover copy for ghosted locked controls (tabs, list items, sidebar nav items). */
export const LOCKED_TOOLTIP =
  'This feature is available only for the Enterprise plan. Click to request an upgrade.'

export const buildEnterprisePrompt = (ctx: EnterpriseRequestContext): string => {
  const want = SOURCE_PHRASES[ctx.source](ctx)
  return `Hi Buddy, I'd like to ${want}. Can you help me request this?`
}

/**
 * Entry point for every gated enterprise CTA: opens the Buddy sidebar with an
 * auto-sent, screen-specific opener. Buddy collects the use case and volume,
 * applies the product gate for the account's region, and only then files the lead.
 *
 * No network call and no request filed from here — see the module header for why
 * the AM-managed shortcut was removed.
 *
 * Imperative (uses the store directly) so it can be called from any event handler
 * without a hook — mirrors the /buddy route and the existing outbound-access /
 * usage-limit CTAs. Synchronous and idempotent: a double-click re-queues the same
 * prompt, and the assistant panel only auto-sends once the chat is idle and not in
 * cooldown.
 */
export const openBuddyEnterpriseRequest = (ctx: EnterpriseRequestContext): void => {
  const { setPendingAssistantPrompt, setIsAssistantOpen } = useAppStore.getState()
  setPendingAssistantPrompt(buildEnterprisePrompt(ctx))
  setIsAssistantOpen(true)
}
